/**
 * Language Context
 *
 * Provides language state and functions throughout the application
 */

'use client';

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import type { LanguageCode, LanguagePreference } from '@/lib/language';
import {
  DEFAULT_LANGUAGE,
  DEFAULT_PREFERENCE,
  LANGUAGE_STORAGE_KEY,
  isRTL,
  isValidLanguagePreference,
  detectBrowserLanguage,
} from '@/lib/language';
import { getTranslation } from '@/locales';

interface LanguageContextType {
  language: LanguageCode;
  preference: LanguagePreference;
  setPreference: (preference: LanguagePreference) => void;
  isRTL: boolean;
  t: (key: string, params?: Record<string, string | number>) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

/**
 * Language Provider Component
 *
 * Wraps the application and provides language state
 */
export function LanguageProvider({ children }: { children: React.ReactNode }) {
  // Track user's preference ('auto' or specific language)
  const [preference, setPreferenceState] = useState<LanguagePreference>(DEFAULT_PREFERENCE);
  // Track actual active language (resolved from preference)
  const [language, setLanguageState] = useState<LanguageCode>(DEFAULT_LANGUAGE);

  // Load preference from localStorage on mount (client-side only)
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const savedPreference = localStorage.getItem(LANGUAGE_STORAGE_KEY);

      if (savedPreference && isValidLanguagePreference(savedPreference)) {
        setPreferenceState(savedPreference);

        // Resolve actual language from preference
        const resolvedLanguage = savedPreference === 'auto'
          ? detectBrowserLanguage()
          : savedPreference;

        setLanguageState(resolvedLanguage);
        updateHtmlAttributes(resolvedLanguage);

        console.log('[Language] Loaded preference:', savedPreference, 'resolved to:', resolvedLanguage);
      } else {
        // No saved preference, use auto-detect
        const detected = detectBrowserLanguage();
        setLanguageState(detected);
        updateHtmlAttributes(detected);
        console.log('[Language] Auto-detected:', detected);
      }
    }
  }, []);

  /**
   * Update HTML lang and dir attributes
   */
  const updateHtmlAttributes = useCallback((lang: LanguageCode) => {
    if (typeof document !== 'undefined') {
      document.documentElement.lang = lang;
      document.documentElement.dir = isRTL(lang) ? 'rtl' : 'ltr';
    }
  }, []);

  /**
   * Set language preference and persist to localStorage
   */
  const setPreference = useCallback(
    (newPreference: LanguagePreference) => {
      setPreferenceState(newPreference);

      // Resolve actual language from preference
      const resolvedLanguage = newPreference === 'auto'
        ? detectBrowserLanguage()
        : newPreference;

      setLanguageState(resolvedLanguage);

      if (typeof window !== 'undefined') {
        localStorage.setItem(LANGUAGE_STORAGE_KEY, newPreference);
      }

      updateHtmlAttributes(resolvedLanguage);
      console.log('[Language] Preference changed to:', newPreference, 'resolved to:', resolvedLanguage);
    },
    [updateHtmlAttributes]
  );

  /**
   * Translation function
   */
  const t = useCallback(
    (key: string, params?: Record<string, string | number>) => {
      return getTranslation(key, language, params);
    },
    [language]
  );

  const contextValue: LanguageContextType = {
    language,
    preference,
    setPreference,
    isRTL: isRTL(language),
    t,
  };

  return (
    <LanguageContext.Provider value={contextValue}>
      {children}
    </LanguageContext.Provider>
  );
}

/**
 * Hook to use language context
 */
export function useLanguage(): LanguageContextType {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}
